import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApplicationRefinement } from '@aurora-pvi/testing';
import type { GlobalFilters, QueryResult } from '@aurora-pvi/contracts';
import { useRepositories } from '../app/providers/DataProvider';
import { useSession } from '../app/providers/SessionProvider';
import { useFilters } from '../app/providers/FiltersProvider';

/**
 * The screen-facing data layer.
 *
 * Every hook here returns `QueryResult<T>` — payload plus the metadata envelope
 * carrying source status, freshness, quality, confidence, scope and allowed
 * actions. Screens are expected to read `meta` as attentively as `data`; a KPI
 * rendered without its provenance is a defect.
 *
 * The current user is part of every cache key. That is not incidental: switching
 * identity must produce genuinely different results, so cached data from a wider
 * span must never be served to a narrower one.
 */

function useQueryContext() {
  const repositories = useRepositories();
  const { currentUser } = useSession();
  const { filters } = useFilters();
  return { repositories, userId: currentUser.id, filters };
}

/** Stable, serialisable key. Filters are sorted so key order cannot vary. */
function key(name: string, userId: string, filters: GlobalFilters, extra?: unknown) {
  const sorted = Object.fromEntries(Object.entries(filters).sort(([a], [b]) => a.localeCompare(b)));
  return extra === undefined ? [name, userId, sorted] : [name, userId, sorted, extra];
}

type Result<T> = UseQueryResult<QueryResult<T>>;

export function usePortfolioSummary(groupBy?: string): Result<
  Awaited<ReturnType<ReturnType<typeof useRepositories>['portfolio']['getSummary']>>['data']
> {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    // `groupBy` is part of the key: two groupings are two different responses,
    // and sharing a cache entry would show the reader the previous grouping's
    // arcs under the new grouping's name.
    queryKey: key('portfolio-summary', userId, filters, groupBy ?? 'department'),
    queryFn: () => repositories.portfolio.getSummary(filters, groupBy),
  }) as never;
}

export function useProductList() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('product-list', userId, filters),
    queryFn: () => repositories.products.list(filters),
  });
}

export function useProduct360(productId: string | undefined) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('product-360', userId, filters, productId),
    queryFn: () => repositories.products.get360(productId as string, filters),
    enabled: Boolean(productId),
  });
}

export function useCandidateList() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('candidates', userId, filters),
    queryFn: () => repositories.discover.listCandidates(filters),
  });
}

export function useAssessmentWizard(candidateId: string | undefined) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('wizard', userId, filters, candidateId),
    queryFn: () => repositories.discover.getWizard(candidateId as string),
    enabled: Boolean(candidateId),
    /*
      No retry, and no cached error.

      A candidate nobody has started an assessment for answers 404, which is a
      state rather than a fault — retrying it asks the same question twice and
      gets the same answer. Left on the default the screen churned: repeated
      fetches with `failureCount` never leaving zero, so the page sat on its
      loading skeleton indefinitely over an answer that had already arrived.

      `gcTime: 0` because the *next* thing that happens on this screen is
      somebody pressing Start, and a cached "no assessment" would be served
      back to them after they had made one.
    */
    retry: false,
    gcTime: 0,
  });
}

export function useAssessmentResult(assessmentId: string | undefined) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('assessment-result', userId, filters, assessmentId),
    queryFn: () => repositories.discover.getResult(assessmentId as string),
    enabled: Boolean(assessmentId),
  });
}

export function useProcessMapping(processRootId?: string) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('process-mapping', userId, filters, processRootId),
    queryFn: () => repositories.mapping.getProcessMapping(filters, processRootId),
  });
}

export function useApplicationMapping() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('application-mapping', userId, filters),
    queryFn: () => repositories.mapping.getApplicationMapping(filters),
  });
}

export function useApplicationPortfolio(
  page: number,
  pageSize: number,
  selectedId?: string,
  refinement?: ApplicationRefinement,
) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    // The refinement is part of the key: it changes which rows come back and
    // what the total counts, so a cached page from a different refinement is a
    // different answer, not the same one.
    queryKey: key('applications', userId, filters, { page, pageSize, selectedId, refinement }),
    queryFn: () =>
      repositories.applications.list(filters, { page, pageSize }, selectedId, refinement),
    // Keeping the previous page visible while the next loads stops a 1,900-row
    // grid from flashing empty on every page change.
    placeholderData: (previous) => previous,
  });
}

export function useDelivery() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('delivery', userId, filters),
    queryFn: () => repositories.delivery.getOverview(filters),
  });
}

export function useInvestment() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('investment', userId, filters),
    queryFn: () => repositories.value.getInvestment(filters),
  });
}

export function useScenarioComparison(scenarioId: string | undefined) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('scenario-compare', userId, filters, scenarioId),
    queryFn: () => repositories.scenarios.compare(scenarioId as string, filters),
    enabled: Boolean(scenarioId),
  });
}

export function useIntegrationOperations() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('integrations', userId, filters),
    queryFn: () => repositories.integrations.getOperations(filters),
  });
}

export function useLineageWorkbench(datasetId?: string) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('lineage', userId, filters, datasetId),
    queryFn: () => repositories.lineage.getWorkbench(datasetId),
  });
}

export function useGovernanceCommand() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('governance', userId, filters),
    queryFn: () => repositories.governance.getCommandCenter(filters),
  });
}

/**
 * The five areas below were built against their own fixture modules and moved
 * onto the port afterwards. They read exactly like the hooks above, which is
 * the point: a screen should not be able to tell where its data came from.
 */

export function useDuplicateReview() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('estate-duplicates', userId, filters),
    queryFn: () => repositories.estate.getDuplicateReview(filters),
  });
}

export function useTechnologyHeatmap() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('estate-heatmap', userId, filters),
    queryFn: () => repositories.estate.getTechnologyHeatmap(),
  });
}

export function usePortfolioComparison(baselineId?: string, comparisonId?: string) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('estate-compare', userId, filters, [baselineId ?? '', comparisonId ?? '']),
    queryFn: () => repositories.estate.comparePortfolio(baselineId, comparisonId),
  });
}

export function useCollaborationWorkspace() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('collaboration', userId, filters),
    queryFn: () => repositories.collaboration.getCollaboration(filters),
  });
}

export function useWatchlist() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('watchlist', userId, filters),
    queryFn: () => repositories.collaboration.getWatchlist(filters),
  });
}

export function useReportsAnalytics() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('reports', userId, filters),
    queryFn: () => repositories.collaboration.getReports(filters),
  });
}

export function useTaxonomyGovernance() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('taxonomy', userId, filters),
    queryFn: () => repositories.collaboration.getTaxonomyGovernance(filters),
  });
}

export function useOutcomeTraceability(outcomeId?: string) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('outcomes', userId, filters, outcomeId),
    queryFn: () => repositories.outcomes.getOutcomeTraceability(outcomeId),
  });
}

/**
 * Identity. None of these four takes the user as an argument — the repository
 * reads it from its own context. The user is still in the cache key, because
 * every one of these projections differs by identity and serving a cached
 * profile to a second person would be the worst possible instance of the
 * general rule stated at the top of this file.
 */

export function useRolesAccess() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('identity-roles', userId, filters),
    queryFn: () => repositories.identity.getRolesAccess(),
  });
}

export function useDelegation() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('identity-delegation', userId, filters),
    queryFn: () => repositories.identity.getDelegation(),
  });
}

export function useSignIn() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('identity-signin', userId, filters),
    queryFn: () => repositories.identity.getSignIn(),
  });
}

export function useUserProfile() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('identity-profile', userId, filters),
    queryFn: () => repositories.identity.getProfile(),
  });
}

export function useProcessCatalog() {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('process-catalog', userId, filters),
    queryFn: () => repositories.process.getCatalog(filters),
  });
}

/**
 * Not gated on `processId`. An absent id is an answer — "no process with that
 * identifier" — and disabling the query instead would leave the screen pending
 * forever on a URL that will never resolve.
 */
export function useProcessDetail(processId: string | undefined) {
  const { repositories, userId, filters } = useQueryContext();
  return useQuery({
    queryKey: key('process-detail', userId, filters, processId ?? null),
    queryFn: () => repositories.process.getDetail(processId, filters),
  });
}
